/**
 * EmployeeTreeSelect.js
 * 조직별 트리 구조로 직원을 선택할 수 있는 컴포넌트
 * 
 * 주요 기능:
 * - 계층적 부서 트리 구조 (mother_dept_code 기반)
 * - 다중 선택 지원 (체크박스)
 * - 검색 기능
 * - 모두 접기/펼치기
 * - 실시간 데이터 업데이트 (이벤트 기반)
 * 
 * 데이터 변경 감지:
 * - 'deptChanged' 이벤트: 부서 데이터 변경 시
 * - 'employeeChanged' 이벤트: 직원 데이터 변경 시
 * - 'deptEmployeeLinkChanged' 이벤트: 부서-직원 연결 변경 시
 * 
 * 자세한 내용은 EmployeeTreeSelect.REPORT.md 참조
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Collapse,
  Divider,
  TextField,
  InputAdornment,
  Paper,
  MenuList,
  MenuItem,
} from '@mui/material';
import { ChevronRight, ChevronDown, Search, Users, ChevronsDown, ChevronsUp } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

// 부서 정렬 순서 정의 (계층 구조)
// Lv0: 더컨설팅그룹 (최상위 - 모든 부서 포함)
// Lv1: THE, 본사, 컨설팅총괄BG, 전략통관BG, 혁신통관BG
// 주의: 스마트전략부문은 전략통관BG의 하위 부서이므로 최상위 목록에 포함하지 않음
const deptOrder = [
  '더컨설팅그룹',
  'THE',
  '본사',
  '컨설팅총괄BG',
  '전략통관BG',
  '혁신통관BG',
];

// 각 부서 내 직원/하위부서 정렬 순서 정의
// Lv1 부서의 Lv2 항목들 (직원 또는 하위 부서)
const employeeOrderByDept = {
  '더컨설팅그룹': [],
  // Lv1 'THE' → Lv2 ['정철', '오석영', '이유지']
  'THE': ['정철', '오석영', '이유지'],
  // Lv1 '본사' → Lv2 ['백준일', '김충호', 'CVI실', '경영지원실', '파견']
  '본사': ['백준일', '김충호', 'CVI실', '경영지원실', '파견'],
  // Lv1 '컨설팅총괄BG' → Lv2 ['조흥제', '컨설팅 부문', 'AEO팀']
  '컨설팅총괄BG': ['조흥제', '컨설팅 부문', 'AEO팀'],
  // Lv1 '전략통관BG' → Lv2 ['오영록', '스마트전략부문', '기획통관팀']
  '전략통관BG': ['오영록', '스마트전략부문', '기획통관팀'],
  // Lv2 '스마트전략부문' → Lv3 ['박진영', '스마트통관팀', '기업전략팀', 'CS파트']
  '스마트전략부문': ['박진영', '스마트통관팀', '기업전략팀', 'CS파트'],
  // Lv1 '혁신통관BG' → Lv2 ['조동백', '최기훈', '창원본부', '부산본부', '구미지사']
  '혁신통관BG': ['조동백', '최기훈', '창원본부', '부산본부', '구미지사'],
};

const EmployeeTreeSelect = ({
  employees = [],
  departments = [],
  selectedEmployees = [],
  onSelectionChange,
  placeholder = '담당자 선택',
  multiple = true,
  showSearch = true,
  onDataRefresh, // 데이터 새로고침 콜백 (선택적)
  allowedEmployeeCodes = null, // 접근 가능한 직원 코드 목록 (null: 모든 직원 활성화, []: 모두 비활성화, [codes]: 해당 직원만 활성화)
}) => {
  const { colors, isDark } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [searchText, setSearchText] = useState('');
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);
  const [selectedAutoCompleteIndex, setSelectedAutoCompleteIndex] = useState(-1);
  
  // 데이터 변경 감지 및 자동 새로고침
  useEffect(() => {
    const handleDeptChanged = () => {
      // 부서 데이터 변경 감지 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] 부서 데이터 변경 감지 - 새로고침 요청');
      if (onDataRefresh) {
        onDataRefresh('departments');
      }
    };
    
    const handleEmployeeChanged = () => {
      // 직원 데이터 변경 감지 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] 직원 데이터 변경 감지 - 새로고침 요청');
      if (onDataRefresh) {
        onDataRefresh('employees');
      }
    };
    
    const handleDeptEmployeeLinkChanged = () => {
      // 부서-직원 연결 데이터 변경 감지 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] 부서-직원 연결 데이터 변경 감지 - 새로고침 요청');
      if (onDataRefresh) {
        onDataRefresh('both'); // 부서와 직원 모두 새로고침
      }
    };
    
    // 전역 이벤트 리스너 등록
    window.addEventListener('deptChanged', handleDeptChanged);
    window.addEventListener('employeeChanged', handleEmployeeChanged);
    window.addEventListener('deptEmployeeLinkChanged', handleDeptEmployeeLinkChanged);
    
    // 정리 함수
    return () => {
      window.removeEventListener('deptChanged', handleDeptChanged);
      window.removeEventListener('employeeChanged', handleEmployeeChanged);
      window.removeEventListener('deptEmployeeLinkChanged', handleDeptEmployeeLinkChanged);
    };
  }, [onDataRefresh]);

  // 직원을 조직별로 그룹화 (계층 구조)
  const employeeTree = useMemo(() => {
    // 입력 데이터 확인 (디버깅 필요 시에만 주석 해제)
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[EmployeeTreeSelect] departments:', departments.length, departments);
    //   console.log('[EmployeeTreeSelect] employees:', employees.length);
    // }
    
    // departments가 비어있어도 employees의 dept_names를 기반으로 부서 구성
    let departmentsToUse = departments;
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // 경고를 한 번만 출력하기 위한 전역 플래그 (컴포넌트 인스턴스별로 관리)
    const componentId = `etree_${departments?.length || 0}_${employees?.length || 0}`;
    const warnKey1 = `_employeeTreeSelectWarned_${componentId}`;
    const warnKey2 = `_employeeTreeSelectWarned2_${componentId}`;
    
    if (!departments || departments.length === 0) {
      // 개발 환경에서만 경고 출력 (컴포넌트별로 한 번만)
      if (isDevelopment && !window[warnKey1]) {
        console.warn('[EmployeeTreeSelect] departments가 비어있습니다.');
        window[warnKey1] = true;
      }
      
      if (!employees || employees.length === 0) {
        if (isDevelopment && !window[warnKey2]) {
          console.warn('[EmployeeTreeSelect] employees도 비어있습니다. 부서 트리를 구성할 수 없습니다.');
          window[warnKey2] = true;
        }
        return [];
      }
      
      // employees의 dept_names를 기반으로 부서 구성 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] employees의 dept_names를 기반으로 부서를 구성합니다.');
      
      // employees에서 부서 정보 추출
      const deptSet = new Set();
      employees.forEach(emp => {
        const deptNames = emp.dept_names ? emp.dept_names.split(', ') : [];
        deptNames.forEach(deptName => {
          if (deptName && deptName.trim()) {
            deptSet.add(deptName.trim());
          }
        });
      });
      
      // departments 배열 생성 (최소한의 정보만)
      const virtualDepartments = Array.from(deptSet).map(deptName => ({
        dept_code: deptName,
        dept_name: deptName,
        mother_dept_code: null,
        mother_dept_name: null,
      }));
      
      if (virtualDepartments.length === 0) {
        if (isDevelopment && !window._employeeTreeSelectWarned3) {
          console.warn('[EmployeeTreeSelect] employees에도 부서 정보가 없습니다.');
          window._employeeTreeSelectWarned3 = true;
        }
        return [];
      }
      
      // 가상 부서 생성 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] 가상 부서 생성:', virtualDepartments.length, '개');
      
      // virtualDepartments를 사용하여 계속 진행
      departmentsToUse = virtualDepartments;
    }
    
    // departments는 있지만 employees가 비어있는 경우에도 부서 트리 구성
    if ((!employees || employees.length === 0) && departmentsToUse && departmentsToUse.length > 0) {
      if (isDevelopment && !window._employeeTreeSelectWarned4) {
        console.warn('[EmployeeTreeSelect] employees가 비어있지만 departments가 있으므로 부서 구조만 표시합니다.');
        window._employeeTreeSelectWarned4 = true;
      }
      // 부서 구조만 표시하기 위해 계속 진행 (employees는 빈 배열로 처리)
    }
    
    const tree = {};
    const deptMap = {};
    const deptNameMap = {}; // dept_name으로 부서 찾기
    
    // 부서 정보 맵 생성
    departmentsToUse.forEach(dept => {
      deptMap[dept.dept_code] = dept;
      deptNameMap[dept.dept_name] = dept;
    });

    // 1단계: 모든 부서를 tree에 먼저 추가 (직원이 없어도)
    departmentsToUse.forEach(dept => {
      if (!tree[dept.dept_name]) {
        tree[dept.dept_name] = {
          dept_code: dept.dept_code,
          dept_name: dept.dept_name,
          employees: [],
          subDepts: [],
          level: 0,
          mother_dept_code: dept.mother_dept_code || null,
          mother_dept_name: dept.mother_dept_name || null,
        };
      }
    });

    // 2단계: 직원을 조직별로 그룹화 (모든 직원 표시, 권한은 체크박스에서 처리)
    // employees가 비어있어도 부서 트리는 구성됨 (직원 없이 부서만 표시)
    if (employees && employees.length > 0) {
      employees.forEach(emp => {
        const deptNames = emp.dept_names ? emp.dept_names.split(', ') : [];
        
        if (deptNames.length === 0) {
          // 부서가 없는 경우 "미지정" 그룹에 추가
          if (!tree['미지정']) {
            tree['미지정'] = {
              dept_code: '미지정',
              dept_name: '미지정',
              employees: [],
              subDepts: [],
              level: 0,
              mother_dept_code: null,
              mother_dept_name: null,
            };
          }
          tree['미지정'].employees.push(emp);
        } else {
          // 각 부서별로 직원 추가
          deptNames.forEach(deptName => {
            if (!tree[deptName]) {
              // 부서 정보가 departments에 없지만 직원에만 있는 경우
              tree[deptName] = {
                dept_code: deptName,
                dept_name: deptName,
                employees: [],
                subDepts: [],
                level: 0,
                mother_dept_code: null,
                mother_dept_name: null,
              };
            }
            tree[deptName].employees.push(emp);
          });
        }
      });
    } else {
      // employees가 비어있어도 부서 구조는 표시
      // employees가 비어있지만 부서 구조는 표시 (디버깅 필요 시에만 주석 해제)
      // console.log('[EmployeeTreeSelect] employees가 비어있지만 부서 구조는 표시합니다.');
    }

    // 3단계: 계층 구조 구성: 하위 부서를 상위 부서에 연결
    // 원본 참조 문제를 방지하기 위해 먼저 모든 부서를 복사
    const treeCopy = {};
    Object.keys(tree).forEach(deptName => {
      const dept = tree[deptName];
      treeCopy[deptName] = {
        ...dept,
        employees: [...dept.employees],
        subDepts: [],
      };
    });
    
    // 복사된 트리에서 계층 구조 구성
    Object.values(treeCopy).forEach(dept => {
      // mother_dept_name이 있고, treeCopy에 해당 부서가 존재하는 경우에만 하위 부서로 연결
      // 순환 참조 방지: 자기 자신을 하위 부서로 추가하지 않음
      if (dept.mother_dept_name && 
          treeCopy[dept.mother_dept_name] && 
          dept.dept_name !== dept.mother_dept_name) {
        // 상위 부서가 있으면 하위 부서로 추가
        if (!treeCopy[dept.mother_dept_name].subDepts.find(sd => sd.dept_name === dept.dept_name)) {
          treeCopy[dept.mother_dept_name].subDepts.push(dept);
        }
        // 최상위 부서 목록에서 제거 (나중에 최상위 부서만 표시하기 위해)
        dept.isSubDept = true;
      }
      // mother_dept_name이 null이거나 빈 문자열이거나, treeCopy에 없는 경우, 또는 자기 자신인 경우는 최상위 부서로 유지
    });
    
    // tree를 treeCopy로 교체
    Object.keys(tree).forEach(deptName => {
      delete tree[deptName];
    });
    Object.keys(treeCopy).forEach(deptName => {
      tree[deptName] = treeCopy[deptName];
    });

    // 계층 구조 확인 (디버깅 필요 시에만 주석 해제)
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[EmployeeTreeSelect] 계층 구조 구성 완료');
    //   const topLevelDepts = Object.values(tree).filter(dept => !dept.isSubDept);
    //   console.log('[EmployeeTreeSelect] 최상위 부서:', topLevelDepts.map(d => d.dept_name));
    //   topLevelDepts.forEach(dept => {
    //     console.log(`[EmployeeTreeSelect] ${dept.dept_name} - 하위 부서:`, dept.subDepts.map(sd => sd.dept_name));
    //   });
    // }

    // 부서별로 직원 및 하위 부서 정렬 (재귀 함수)
    // 순환 참조 방지를 위한 방문 추적 Set (각 최상위 부서별로 초기화)
    const sortDept = (dept, visitedDepts = new Set(), depth = 0) => {
      // 최대 깊이 제한 (안전장치)
      if (depth > 10) {
        if (isDevelopment) {
          console.warn(`[EmployeeTreeSelect] 최대 깊이 초과: ${dept.dept_name} (${dept.dept_code})`);
        }
        return {
          ...dept,
          employees: [...dept.employees],
          subDepts: [],
        };
      }
      
      // 순환 참조 방지: 이미 방문한 부서는 건너뛰기
      if (visitedDepts.has(dept.dept_code)) {
        if (isDevelopment) {
          console.warn(`[EmployeeTreeSelect] 순환 참조 감지: ${dept.dept_name} (${dept.dept_code})`);
        }
        return {
          ...dept,
          employees: [...dept.employees],
          subDepts: [],
        };
      }
      
      visitedDepts.add(dept.dept_code);
      
      try {
        const employeeOrder = employeeOrderByDept[dept.dept_name] || [];
        
        // 각 부서의 직원을 정의된 순서로 정렬
        const sortedEmployees = [...dept.employees].sort((a, b) => {
          const nameA = (a.employee_name || '').trim();
          const nameB = (b.employee_name || '').trim();
          
          // 정의된 순서가 있으면 그 순서 사용
          const indexA = employeeOrder.indexOf(nameA);
          const indexB = employeeOrder.indexOf(nameB);
          
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          
          // 정의된 순서가 없으면 employee_code 숫자 기준으로 정렬
          const numA = parseInt(a.employee_code.match(/\d+/)?.[0] || '999999', 10);
          const numB = parseInt(b.employee_code.match(/\d+/)?.[0] || '999999', 10);
          return numA - numB;
        });

        // 하위 부서 정렬 및 재귀 처리
        // subDepts를 복사하여 원본 참조 문제 방지
        // 순환 참조 방지: 자기 자신을 하위 부서에서 제외
        const subDeptsCopy = (dept.subDepts ? [...dept.subDepts] : [])
          .filter(subDept => subDept.dept_name !== dept.dept_name && subDept.dept_code !== dept.dept_code);
        
        const sortedSubDepts = subDeptsCopy
          .map(subDept => {
            // 깊은 복사로 원본 참조 문제 방지
            const subDeptCopy = {
              ...subDept,
              employees: [...subDept.employees],
              subDepts: subDept.subDepts ? [...subDept.subDepts] : [],
            };
            return sortDept(subDeptCopy, visitedDepts, depth + 1);
          })
          .sort((a, b) => {
            const indexA = employeeOrder.indexOf(a.dept_name);
            const indexB = employeeOrder.indexOf(b.dept_name);
            
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            
            // 정의된 순서가 없으면 dept_code 숫자 기준으로 정렬
            const numA = parseInt(a.dept_code.match(/\d+/)?.[0] || '999999', 10);
            const numB = parseInt(b.dept_code.match(/\d+/)?.[0] || '999999', 10);
            return numA - numB;
          });

        visitedDepts.delete(dept.dept_code);
        
        return {
          ...dept,
          employees: sortedEmployees,
          subDepts: sortedSubDepts,
        };
      } catch (error) {
        console.error(`[EmployeeTreeSelect] sortDept 오류 (${dept.dept_name}):`, error);
        visitedDepts.delete(dept.dept_code);
        return {
          ...dept,
          employees: [...dept.employees],
          subDepts: [],
        };
      }
    };

    // 최상위 부서만 필터링 (하위 부서가 아닌 것들)
    let topLevelDepts = Object.values(tree).filter(dept => !dept.isSubDept);
    
    // THE 부서가 최상위 부서로 유지되도록 보장 (순환 참조 방지)
    topLevelDepts.forEach(dept => {
      if (dept.dept_name === 'THE') {
        // THE 부서의 하위 부서 목록에서 자기 자신 제거
        if (dept.subDepts) {
          dept.subDepts = dept.subDepts.filter(subDept => 
            subDept.dept_name !== 'THE' && subDept.dept_code !== dept.dept_code
          );
        }
        // THE 부서를 최상위 부서로 강제 설정
        dept.isSubDept = false;
      }
    });
    
    // 최상위 부서 확인 (경고만 유지)
    if (topLevelDepts.length === 0) {
      if (isDevelopment && !window._employeeTreeSelectWarned5) {
        console.warn('[EmployeeTreeSelect] 최상위 부서가 없습니다. 모든 부서가 하위 부서로 분류되었습니다.');
        window._employeeTreeSelectWarned5 = true;
      }
    }
    
    // 최상위 부서가 없으면 대체 방법으로 최상위 부서 찾기
    if (topLevelDepts.length === 0) {
      // 방법 1: mother_dept_name이 null이거나 빈 문자열인 부서
      topLevelDepts = Object.values(tree).filter(dept => 
        !dept.mother_dept_name || 
        (typeof dept.mother_dept_name === 'string' && dept.mother_dept_name.trim() === '') || 
        !tree[dept.mother_dept_name]
      );
      
      // 방법 2: 여전히 없으면 deptOrder에 정의된 부서 중 tree에 있는 것들
      if (topLevelDepts.length === 0) {
        topLevelDepts = deptOrder
          .map(deptName => tree[deptName])
          .filter(dept => dept !== undefined);
      }
      
      // 방법 3: 여전히 없으면 모든 부서를 표시 (평면 구조)
      if (topLevelDepts.length === 0) {
        if (isDevelopment && !window._employeeTreeSelectWarned6) {
          console.warn('[EmployeeTreeSelect] 최상위 부서를 찾을 수 없어 모든 부서를 평면 구조로 표시합니다.');
          window._employeeTreeSelectWarned6 = true;
        }
        topLevelDepts = Object.values(tree);
      }
      
      // 대체 방법으로 찾은 최상위 부서 (디버깅 필요 시에만 주석 해제)
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('[EmployeeTreeSelect] 대체 방법으로 찾은 최상위 부서 수:', topLevelDepts.length);
      //   console.log('[EmployeeTreeSelect] 대체 최상위 부서:', topLevelDepts.map(d => d.dept_name));
      // }
    }
    
    const deptsToSort = topLevelDepts;
    
    // 최상위 부서 정렬 (각 부서마다 새로운 visitedDepts 생성)
    const sortedDepts = deptsToSort.map(dept => {
      const visitedDepts = new Set();
      return sortDept(dept, visitedDepts, 0);
    }).sort((a, b) => {
      // "미지정"은 맨 아래로
      if (a.dept_code === '미지정') return 1;
      if (b.dept_code === '미지정') return -1;
      
      // 정의된 부서 순서 사용
      const indexA = deptOrder.indexOf(a.dept_name);
      const indexB = deptOrder.indexOf(b.dept_name);
      
      // 부서 정렬 확인 (디버깅 필요 시에만 주석 해제)
      // if (process.env.NODE_ENV === 'development' && (indexA === -1 || indexB === -1)) {
      //   if (indexA === -1) console.log(`[EmployeeTreeSelect] 정의되지 않은 부서: ${a.dept_name}`);
      //   if (indexB === -1) console.log(`[EmployeeTreeSelect] 정의되지 않은 부서: ${b.dept_name}`);
      // }
      
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // 정의된 순서가 없으면 dept_code 숫자 기준으로 정렬
      const numA = parseInt(a.dept_code.match(/\d+/)?.[0] || '999999', 10);
      const numB = parseInt(b.dept_code.match(/\d+/)?.[0] || '999999', 10);
      return numA - numB;
    });

    // 최종 결과 확인 (디버깅 필요 시에만 주석 해제)
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[EmployeeTreeSelect] 최종 정렬된 부서 수:', sortedDepts.length);
    //   console.log('[EmployeeTreeSelect] 최종 부서 목록:', sortedDepts.map(d => d.dept_name));
    // }
    
    return sortedDepts;
  }, [employees, departments, allowedEmployeeCodes]);

  // 검색 필터링 (계층 구조 고려)
  const filteredTree = useMemo(() => {
    if (!searchText) return employeeTree;
    
    const search = searchText.toLowerCase();
    
    // 재귀적으로 부서와 직원 필터링
    const filterDept = (dept) => {
      // null 체크
      if (!dept || !dept.dept_name) {
        return null;
      }
      
      // employees가 없으면 빈 배열로 초기화 (원본 수정하지 않고 새 객체 반환)
      const employees = dept.employees || [];
      
      // 부서명 검색
      const deptNameMatch = dept.dept_name.toLowerCase().includes(search);
      
      // 직원 필터링
      const filteredEmployees = employees.filter(emp => {
        if (!emp) return false;
        const name = (emp.employee_name || '').toLowerCase();
        const code = (emp.employee_code || '').toLowerCase();
        return name.includes(search) || code.includes(search);
      });
      
      // 하위 부서 필터링 (재귀)
      const filteredSubDepts = dept.subDepts && dept.subDepts.length > 0
        ? dept.subDepts
            .map(subDept => filterDept(subDept))
            .filter(subDept => subDept !== null && subDept !== undefined)
            .filter(subDept => 
              (subDept.employees && subDept.employees.length > 0) || 
              (subDept.subDepts && subDept.subDepts.length > 0)
            )
        : [];
      
      // 부서명이 매치되거나, 직원이 있거나, 하위 부서가 있으면 포함
      const hasMatch = deptNameMatch || filteredEmployees.length > 0 || filteredSubDepts.length > 0;
      
      if (!hasMatch) {
        return null;
      }
      
      // 필터링 후에도 정의된 순서로 정렬 유지
      const employeeOrder = employeeOrderByDept[dept.dept_name] || [];
      const sortedEmployees = [...filteredEmployees].sort((a, b) => {
        if (!a || !b) return 0;
        const nameA = (a.employee_name || '').trim();
        const nameB = (b.employee_name || '').trim();
        
        // 정의된 순서가 있으면 그 순서 사용
        const indexA = employeeOrder.indexOf(nameA);
        const indexB = employeeOrder.indexOf(nameB);
        
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        // 정의된 순서가 없으면 employee_code 숫자 기준으로 정렬
        const numA = parseInt(a.employee_code.match(/\d+/)?.[0] || '999999', 10);
        const numB = parseInt(b.employee_code.match(/\d+/)?.[0] || '999999', 10);
        return numA - numB;
      });
      
      // 부서명이 매치된 경우 직원이 없어도 포함 (하위 부서가 있을 수 있음)
      // 부서명 매치 정보를 객체에 포함하여 최종 필터링에서 사용
      return {
        ...dept,
        employees: sortedEmployees,
        subDepts: filteredSubDepts,
        _hasDeptNameMatch: deptNameMatch, // 내부 플래그: 부서명이 매치되었는지
      };
    };
    
    const filtered = employeeTree
      .map(dept => filterDept(dept))
      .filter(dept => dept !== null && dept !== undefined)
      .filter(dept => {
        // 부서명이 매치되었거나, 직원이 있거나, 하위 부서가 있으면 포함
        const hasEmployees = dept.employees && dept.employees.length > 0;
        const hasSubDepts = dept.subDepts && dept.subDepts.length > 0;
        const hasDeptNameMatch = dept._hasDeptNameMatch || false;
        
        return hasDeptNameMatch || hasEmployees || hasSubDepts;
      })
      .map(dept => {
        // 내부 플래그 제거 (외부에 노출하지 않음)
        const { _hasDeptNameMatch, ...cleanDept } = dept;
        return cleanDept;
      });
    
    return filtered;
  }, [employeeTree, searchText]);

  // 자동완성 후보 목록 생성 (직원명, 부서명 등)
  const autoCompleteSuggestions = useMemo(() => {
    if (!searchText || searchText.trim() === '') {
      return [];
    }

    const search = searchText.toLowerCase().trim();
    const suggestions = [];

    // 직원명/직원코드 검색
    employees.forEach(emp => {
      if (!emp) return;
      const name = (emp.employee_name || '').toLowerCase();
      const code = (emp.employee_code || '').toLowerCase();
      if (name.includes(search) || code.includes(search)) {
        suggestions.push({
          type: 'employee',
          label: `${emp.employee_name || emp.employee_code} (${emp.employee_code})`,
          value: emp.employee_name || emp.employee_code,
          employeeCode: emp.employee_code,
          employee: emp,
        });
      }
    });

    // 부서명 검색
    departments.forEach(dept => {
      if (!dept || !dept.dept_name) return;
      const deptName = dept.dept_name.toLowerCase();
      if (deptName.includes(search)) {
        suggestions.push({
          type: 'department',
          label: dept.dept_name,
          value: dept.dept_name,
          deptCode: dept.dept_code,
          department: dept,
        });
      }
    });

    // 중복 제거 및 정렬
    const uniqueSuggestions = suggestions.filter((item, index, self) =>
      index === self.findIndex(t => t.label === item.label)
    );

    return uniqueSuggestions.slice(0, 10); // 최대 10개만 표시
  }, [searchText, employees, departments]);

  // 검색어 변경 시 자동완성 드롭다운 열기/닫기 (autoCompleteSuggestions 정의 이후에 위치)
  useEffect(() => {
    if (searchText.trim().length > 0 && autoCompleteSuggestions.length > 0) {
      setAutoCompleteOpen(true);
    } else {
      setAutoCompleteOpen(false);
      setSelectedAutoCompleteIndex(-1);
    }
  }, [searchText, autoCompleteSuggestions.length]);

  // 선택된 직원 수 계산
  const selectedCount = selectedEmployees.length;

  // 직원의 체크박스 활성화 여부 확인
  const isEmployeeEnabled = (employeeCode) => {
    if (allowedEmployeeCodes === null) {
      // null이면 모든 직원 활성화 (Root/Admin)
      return true;
    }
    if (allowedEmployeeCodes.length === 0) {
      // 빈 배열이면 모든 직원 비활성화
      return false;
    }
    // 배열에 포함된 직원만 활성화
    return allowedEmployeeCodes.includes(employeeCode);
  };

  // 부서의 체크박스 활성화 여부 확인 (부서 내 활성화된 직원이 하나라도 있으면 활성화)
  // 직원이 없는 부서도 표시되도록 허용 (allowedEmployeeCodes가 null이면 모든 부서 활성화)
  const isDeptEnabled = (dept) => {
    // allowedEmployeeCodes가 null이면 모든 부서 활성화 (직원이 없어도 부서는 표시)
    if (allowedEmployeeCodes === null) {
      return true;
    }
    
    // 직접 소속 직원 중 활성화된 직원이 있는지 확인
    const hasEnabledEmployee = dept.employees.some(emp => isEmployeeEnabled(emp.employee_code));
    if (hasEnabledEmployee) return true;
    
    // 하위 부서 중 활성화된 부서가 있는지 확인
    if (dept.subDepts && dept.subDepts.length > 0) {
      return dept.subDepts.some(subDept => isDeptEnabled(subDept));
    }
    
    // 직원이 없는 부서도 표시 (부서 구조 유지)
    // allowedEmployeeCodes가 빈 배열이 아니면 부서는 표시
    return allowedEmployeeCodes.length > 0;
  };

  // 전체 선택/해제 (활성화된 직원만 선택)
  const handleSelectAll = (checked) => {
    if (checked) {
      // 활성화된 직원만 선택
      const enabledEmployeeCodes = employees
        .filter(emp => isEmployeeEnabled(emp.employee_code))
        .map(emp => emp.employee_code);
      onSelectionChange(enabledEmployeeCodes);
    } else {
      onSelectionChange([]);
    }
  };

  // 부서 전체 선택/해제 (하위 부서 포함, 활성화된 직원만)
  const handleSelectDept = (dept, checked) => {
    if (!isDeptEnabled(dept)) return; // 비활성화된 부서는 선택 불가
    
    if (multiple) {
      // 다중 선택 모드
      // 직접 소속 직원 중 활성화된 직원만
      let deptEmployeeCodes = dept.employees
        .filter(emp => isEmployeeEnabled(emp.employee_code))
        .map(emp => emp.employee_code);
      
      // 하위 부서의 활성화된 직원도 포함
      const getAllSubDeptEmployees = (subDepts) => {
        subDepts.forEach(subDept => {
          const enabledEmployees = subDept.employees
            .filter(emp => isEmployeeEnabled(emp.employee_code))
            .map(emp => emp.employee_code);
          deptEmployeeCodes.push(...enabledEmployees);
          if (subDept.subDepts && subDept.subDepts.length > 0) {
            getAllSubDeptEmployees(subDept.subDepts);
          }
        });
      };
      if (dept.subDepts && dept.subDepts.length > 0) {
        getAllSubDeptEmployees(dept.subDepts);
      }
      
      if (checked) {
        // 부서의 활성화된 모든 직원 추가 (중복 제거)
        const newSelection = [...new Set([...selectedEmployees, ...deptEmployeeCodes])];
        onSelectionChange(newSelection);
      } else {
        // 부서의 모든 직원 제거
        const newSelection = selectedEmployees.filter(code => !deptEmployeeCodes.includes(code));
        onSelectionChange(newSelection);
      }
    } else {
      // 단일 선택 모드: 첫 번째 활성화된 직원만 선택
      const firstEnabledEmployee = dept.employees.find(emp => isEmployeeEnabled(emp.employee_code));
      if (checked && firstEnabledEmployee) {
        onSelectionChange([firstEnabledEmployee.employee_code]);
      } else {
        onSelectionChange([]);
      }
    }
  };

  // 개별 직원 선택/해제
  const handleSelectEmployee = (employeeCode, checked) => {
    if (!isEmployeeEnabled(employeeCode)) return; // 비활성화된 직원은 선택 불가
    
    if (multiple) {
      // 다중 선택 모드
      if (checked) {
        onSelectionChange([...selectedEmployees, employeeCode]);
      } else {
        onSelectionChange(selectedEmployees.filter(code => code !== employeeCode));
      }
    } else {
      // 단일 선택 모드
      if (checked) {
        onSelectionChange([employeeCode]);
      } else {
        onSelectionChange([]);
      }
    }
  };

  // 부서의 선택 상태 확인 (하위 부서 포함, 활성화된 직원만 고려)
  const getDeptCheckState = (dept) => {
    // 직접 소속 직원 중 활성화된 직원만
    const enabledDeptEmployeeCodes = dept.employees
      .filter(emp => isEmployeeEnabled(emp.employee_code))
      .map(emp => emp.employee_code);
    
    // 하위 부서의 활성화된 직원도 포함
    let allEnabledEmployeeCodes = [...enabledDeptEmployeeCodes];
    const getAllSubDeptEmployees = (subDepts) => {
      subDepts.forEach(subDept => {
        const enabledEmployees = subDept.employees
          .filter(emp => isEmployeeEnabled(emp.employee_code))
          .map(emp => emp.employee_code);
        allEnabledEmployeeCodes.push(...enabledEmployees);
        if (subDept.subDepts && subDept.subDepts.length > 0) {
          getAllSubDeptEmployees(subDept.subDepts);
        }
      });
    };
    if (dept.subDepts && dept.subDepts.length > 0) {
      getAllSubDeptEmployees(dept.subDepts);
    }
    
    // 활성화된 직원 중 선택된 직원
    const allSelected = allEnabledEmployeeCodes.filter(code => selectedEmployees.includes(code));
    
    if (allEnabledEmployeeCodes.length === 0) return 'unchecked'; // 활성화된 직원이 없으면 unchecked
    if (allSelected.length === 0) return 'unchecked';
    if (allSelected.length === allEnabledEmployeeCodes.length) return 'checked';
    return 'indeterminate';
  };

  // 전체 선택 상태 확인
  const getAllCheckState = () => {
    // 활성화된 직원만 고려
    const enabledEmployeeCodes = employees
      .filter(emp => isEmployeeEnabled(emp.employee_code))
      .map(emp => emp.employee_code);
    
    if (enabledEmployeeCodes.length === 0) return 'unchecked'; // 활성화된 직원이 없으면 unchecked
    
    const selectedEnabled = enabledEmployeeCodes.filter(code => selectedEmployees.includes(code));
    
    if (selectedEnabled.length === 0) return 'unchecked';
    if (selectedEnabled.length === enabledEmployeeCodes.length) return 'checked';
    return 'indeterminate';
  };

  // 부서 토글
  const handleToggleDept = (deptName) => {
    setExpandedDepts(prev => ({
      ...prev,
      [deptName]: !prev[deptName],
    }));
  };

  // 모든 부서 이름 수집 (재귀)
  const getAllDeptNames = (depts) => {
    const names = [];
    depts.forEach(dept => {
      names.push(dept.dept_name);
      if (dept.subDepts && dept.subDepts.length > 0) {
        names.push(...getAllDeptNames(dept.subDepts));
      }
    });
    return names;
  };

  // 모두 접기/펼치기
  const handleToggleAll = () => {
    const allDeptNames = getAllDeptNames(filteredTree);
    const allExpanded = allDeptNames.every(name => expandedDepts[name] === true);
    
    if (allExpanded) {
      // 모두 접기
      setExpandedDepts({});
    } else {
      // 모두 펼치기
      const newExpanded = {};
      allDeptNames.forEach(name => {
        newExpanded[name] = true;
      });
      setExpandedDepts(newExpanded);
    }
  };

  // 모두 펼쳐져 있는지 확인
  const isAllExpanded = () => {
    if (filteredTree.length === 0) return false;
    const allDeptNames = getAllDeptNames(filteredTree);
    return allDeptNames.length > 0 && allDeptNames.every(name => expandedDepts[name] === true);
  };

  // 표시 텍스트 생성
  const getDisplayText = () => {
    if (selectedCount === 0) return placeholder;
    if (selectedCount === 1) {
      const emp = employees.find(e => e.employee_code === selectedEmployees[0]);
      return emp ? (emp.employee_name || emp.employee_code) : placeholder;
    }
    return `${selectedCount}명 선택됨`;
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          minWidth: 150,
          justifyContent: 'space-between',
          textTransform: 'none',
          color: colors.primaryText,
          borderColor: colors.primaryBorder,
          '&:hover': {
            borderColor: colors.accent,
            backgroundColor: colors.hoverBg,
          },
        }}
      >
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getDisplayText()}
        </Typography>
        <ChevronRight size={16} style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      </Button>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 600,
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            mt: 0.5,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          {/* 검색 입력 */}
          {showSearch && (
            <Box sx={{ position: 'relative', mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="직원 검색..."
                value={searchText}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  setSelectedAutoCompleteIndex(-1);
                  // 자동완성은 useEffect에서 처리됨
                }}
                onFocus={() => {
                  // 포커스 시 검색어가 있으면 자동완성 열기 (useEffect에서 처리됨)
                }}
                onBlur={() => {
                  // 약간의 지연을 두어 클릭 이벤트가 먼저 처리되도록
                  setTimeout(() => setAutoCompleteOpen(false), 200);
                }}
                onKeyDown={(e) => {
                  if (!autoCompleteOpen || autoCompleteSuggestions.length === 0) {
                    // 자동완성이 열려있지 않으면 엔터로 검색 확정
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.target.blur();
                    }
                    return;
                  }

                  // 자동완성 열려있을 때 키보드 처리
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedAutoCompleteIndex(prev => 
                      prev < autoCompleteSuggestions.length - 1 ? prev + 1 : prev
                    );
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedAutoCompleteIndex(prev => prev > 0 ? prev - 1 : -1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (selectedAutoCompleteIndex >= 0 && selectedAutoCompleteIndex < autoCompleteSuggestions.length) {
                      const selected = autoCompleteSuggestions[selectedAutoCompleteIndex];
                      setAutoCompleteOpen(false);
                      setSelectedAutoCompleteIndex(-1);
                      // 선택된 항목이 직원이면 해당 직원만 선택 (기존 선택 초기화)
                      if (selected.type === 'employee' && selected.employeeCode) {
                        // 검색으로 직원 선택 (디버깅 필요 시에만 주석 해제)
                        // console.log('[EmployeeTreeSelect] 검색으로 직원 선택 (Enter):', selected.employeeCode, '- 기존 선택 초기화');
                        onSelectionChange([selected.employeeCode]);
                        // 검색어 초기화 및 Popover 닫기
                        setSearchText('');
                        setAnchorEl(null);
                      }
                    } else if (autoCompleteSuggestions.length > 0) {
                      // 선택된 항목이 없으면 첫 번째 항목 선택 (기존 선택 초기화)
                      const first = autoCompleteSuggestions[0];
                      setAutoCompleteOpen(false);
                      setSelectedAutoCompleteIndex(-1);
                      if (first.type === 'employee' && first.employeeCode) {
                        // 검색으로 직원 선택 (디버깅 필요 시에만 주석 해제)
                        // console.log('[EmployeeTreeSelect] 검색으로 직원 선택 (Enter - 첫 번째):', first.employeeCode, '- 기존 선택 초기화');
                        onSelectionChange([first.employeeCode]);
                        // 검색어 초기화 및 Popover 닫기
                        setSearchText('');
                        setAnchorEl(null);
                      }
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setAutoCompleteOpen(false);
                    setSelectedAutoCompleteIndex(-1);
                    // Popover도 닫기
                    setAnchorEl(null);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color={colors.secondaryText} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? '#1C1D22' : '#F5F5F5',
                    '& fieldset': {
                      borderColor: colors.primaryBorder,
                    },
                  },
                }}
              />
              
              {/* 자동완성 드롭다운 */}
              {autoCompleteOpen && autoCompleteSuggestions.length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    zIndex: 1300,
                    maxHeight: 200,
                    overflow: 'auto',
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <MenuList dense>
                    {autoCompleteSuggestions.map((suggestion, index) => (
                      <MenuItem
                        key={`${suggestion.type}-${suggestion.value}-${index}`}
                        selected={index === selectedAutoCompleteIndex}
                        onClick={() => {
                          setAutoCompleteOpen(false);
                          setSelectedAutoCompleteIndex(-1);
                          // 선택된 항목이 직원이면 해당 직원만 선택 (기존 선택 초기화)
                          if (suggestion.type === 'employee' && suggestion.employeeCode) {
                            // 검색으로 직원 선택 (디버깅 필요 시에만 주석 해제)
                            // console.log('[EmployeeTreeSelect] 검색으로 직원 선택 (클릭):', suggestion.employeeCode, '- 기존 선택 초기화');
                            onSelectionChange([suggestion.employeeCode]);
                            // 검색어 초기화 및 Popover 닫기
                            setSearchText('');
                            setAnchorEl(null);
                          }
                        }}
                        sx={{
                          py: 0.75,
                          px: 1.5,
                          backgroundColor: index === selectedAutoCompleteIndex 
                            ? (isDark ? '#2A2B30' : '#E3F2FD')
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isDark ? '#2A2B30' : '#F5F5F5',
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: suggestion.type === 'employee' ? colors.primaryText : colors.secondaryText,
                            fontWeight: suggestion.type === 'employee' ? 500 : 400,
                          }}
                        >
                          {suggestion.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              )}
            </Box>
          )}

          {/* 모두 접기/펼치기 버튼 */}
          {filteredTree.length > 0 && (
            <Box sx={{ mb: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleToggleAll}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: colors.secondaryText,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: colors.hoverBg,
                    color: colors.primaryText,
                  },
                }}
                startIcon={isAllExpanded() ? <ChevronsUp size={14} /> : <ChevronsDown size={14} />}
              >
                {isAllExpanded() ? '모두 접기' : '모두 펼치기'}
              </Button>
            </Box>
          )}

          {/* 전체 선택 (다중 선택 모드에서만 표시) */}
          {multiple && (
            <>
              <ListItemButton
                dense
                onClick={() => handleSelectAll(getAllCheckState() !== 'checked')}
                sx={{
                  mb: 0.25,
                  py: 0.5,
                  minHeight: 'auto',
                  borderRadius: 1,
                  pl: 1, // 부서 헤더와 동일한 왼쪽 여백 (level 0일 때와 동일)
                  pr: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: colors.hoverBg,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                  {/* ListItemIcon을 사용하여 부서 헤더와 동일한 구조로 맞춤 */}
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <Box sx={{ width: 16 }} /> {/* chevron 공간 (부서 헤더와 동일한 너비) */}
                  </ListItemIcon>
                  {/* Users 아이콘 공간 (부서 헤더와 동일한 구조) */}
                  <Box sx={{ width: 16, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: colors.primaryText,
                      fontSize: '0.875rem',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    전체 선택
                  </Typography>
                </Box>
                <Checkbox
                  checked={getAllCheckState() === 'checked'}
                  indeterminate={getAllCheckState() === 'indeterminate'}
                  size="small"
                  disabled={allowedEmployeeCodes !== null && allowedEmployeeCodes.length === 0} // 모든 직원이 비활성화된 경우만 전체 선택 비활성화
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll(e.target.checked);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    color: colors.accent,
                    padding: 0.5,
                    ml: 0.5, // 부서 헤더 및 직원 체크박스와 동일한 간격
                    flexShrink: 0,
                    '&.Mui-checked': {
                      color: colors.accent,
                    },
                  }}
                />
              </ListItemButton>
              <Divider sx={{ my: 0.25, borderColor: colors.primaryBorder }} />
            </>
          )}

          {/* 조직별 트리 */}
          <List dense sx={{ maxHeight: 450, overflow: 'auto', py: 0.25 }}>
            {filteredTree.length === 0 && employeeTree.length === 0 && employees.length === 0 && departments.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  데이터를 불러오는 중...
                </Typography>
              </Box>
            )}
            {filteredTree.length === 0 && employeeTree.length > 0 && searchText && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  검색 결과가 없습니다.
                </Typography>
              </Box>
            )}
            {filteredTree.map((dept) => {
              // 재귀적으로 부서 트리 렌더링
              const renderDeptTree = (deptNode, level = 0) => {
                const deptCheckState = getDeptCheckState(deptNode);
                const isExpanded = expandedDepts[deptNode.dept_name] === true;
                
                // 정의된 순서에 따라 직원과 하위 부서 정렬
                const employeeOrder = employeeOrderByDept[deptNode.dept_name] || [];
                
                // 하위 항목 정렬: 정의된 순서 우선, 그 다음 직원, 그 다음 하위 부서
                const sortedItems = [];
                
                // 정의된 순서에 따라 항목 배치
                employeeOrder.forEach(itemName => {
                  // 직원인지 확인
                  const emp = deptNode.employees.find(e => e.employee_name === itemName);
                  if (emp) {
                    sortedItems.push({ type: 'employee', data: emp });
                  } else {
                    // 하위 부서인지 확인
                    const subDept = deptNode.subDepts?.find(sd => sd.dept_name === itemName);
                    if (subDept) {
                      sortedItems.push({ type: 'subDept', data: subDept });
                    }
                  }
                });
                
                // 정의된 순서에 없는 직원 추가
                deptNode.employees.forEach(emp => {
                  if (!employeeOrder.includes(emp.employee_name)) {
                    sortedItems.push({ type: 'employee', data: emp });
                  }
                });
                
                // 정의된 순서에 없는 하위 부서 추가
                deptNode.subDepts?.forEach(subDept => {
                  if (!employeeOrder.includes(subDept.dept_name)) {
                    sortedItems.push({ type: 'subDept', data: subDept });
                  }
                });
                
                // 총 직원 수 계산 (하위 부서 포함)
                const getTotalEmployeeCount = (d) => {
                  let count = d.employees.length;
                  if (d.subDepts) {
                    d.subDepts.forEach(sd => {
                      count += getTotalEmployeeCount(sd);
                    });
                  }
                  return count;
                };
                const totalCount = getTotalEmployeeCount(deptNode);
                
                return (
                  <React.Fragment key={deptNode.dept_code}>
                    {/* 부서 헤더 */}
                    <ListItemButton
                      dense
                      onClick={() => handleToggleDept(deptNode.dept_name)}
                      sx={{
                        borderRadius: 1,
                        py: 0.5,
                        minHeight: 'auto',
                        pl: level * 2.5 + 1, // 레벨에 따라 들여쓰기 (1, 3.5, 6, 8.5...)
                        pr: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: colors.hoverBg,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          {(deptNode.employees.length > 0 || (deptNode.subDepts && deptNode.subDepts.length > 0)) ? (
                            isExpanded ? (
                              <ChevronDown size={16} color={colors.secondaryText} />
                            ) : (
                              <ChevronRight size={16} color={colors.secondaryText} />
                            )
                          ) : (
                            <Box sx={{ width: 16 }} />
                          )}
                        </ListItemIcon>
                        <Users size={16} color={colors.secondaryText} style={{ flexShrink: 0 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: colors.primaryText, 
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {deptNode.dept_name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.secondaryText,
                            fontSize: '0.75rem',
                            flexShrink: 0,
                            ml: 0.5,
                          }}
                        >
                          ({totalCount})
                        </Typography>
                      </Box>
                      <Checkbox
                        checked={deptCheckState === 'checked'}
                        indeterminate={deptCheckState === 'indeterminate'}
                        size="small"
                        disabled={!isDeptEnabled(deptNode)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectDept(deptNode, e.target.checked);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          color: colors.accent,
                          padding: 0.5,
                          ml: 0.5, // 직원 체크박스와 동일한 간격으로 통일
                          flexShrink: 0,
                          '&.Mui-checked': {
                            color: colors.accent,
                          },
                        }}
                      />
                    </ListItemButton>

                    {/* 부서 내 항목 목록 (직원 + 하위 부서) */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List dense sx={{ pl: (level + 1) * 2.5, py: 0.25 }}>
                        {sortedItems.map((item, idx) => {
                          if (item.type === 'employee') {
                            const emp = item.data;
                            const isSelected = selectedEmployees.includes(emp.employee_code);
                            const displayName = emp.employee_name || emp.employee_code;
                            
                            return (
                              <ListItemButton
                                key={emp.employee_code}
                                dense
                                onClick={() => handleSelectEmployee(emp.employee_code, !isSelected)}
                                sx={{
                                  borderRadius: 1,
                                  py: 0.25,
                                  minHeight: 'auto',
                                  pl: 1.5,
                                  pr: 1,
                                  mb: 0.125,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  '&:hover': {
                                    backgroundColor: colors.hoverBg,
                                  },
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: colors.primaryText,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {displayName}
                                </Typography>
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  disabled={!isEmployeeEnabled(emp.employee_code)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectEmployee(emp.employee_code, e.target.checked);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  sx={{
                                    color: colors.accent,
                                    padding: 0.5,
                                    ml: 0.5,
                                    flexShrink: 0,
                                    '&.Mui-checked': {
                                      color: colors.accent,
                                    },
                                  }}
                                />
                              </ListItemButton>
                            );
                          } else if (item.type === 'subDept') {
                            // 하위 부서 재귀 렌더링
                            return renderDeptTree(item.data, level + 1);
                          }
                          return null;
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              };
              
              return renderDeptTree(dept);
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default EmployeeTreeSelect;

